import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Paper,
    TextField,
    Card,
    CardContent,
    useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';

/* ─────────────────────────────────────────────────────────────
   PaymentTrackingDashboard
   • Stacked bar chart by PaymentSource (all sites, ordered)
   • Distinct colours (no dark blue)
   • All money shown as $1,234.56
   • Dark-mode tooltips / labels readable
   ──────────────────────────────────────────────────────────── */
const PaymentTrackingDashboard = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const text = theme.palette.text.primary;

    /* 💵 formatter – US currency */
    const usdFmt = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    });

    /* Colour map (avoid dark blue) */
    const sourceColors = {
        Stripe: '#4dabf7',                 // light blue
        ACH: theme.palette.warning.main, // amber / orange
        Venmo: '#8e44ad',                 // purple
        CashApp: '#27ae60',                 // green
        PayPal: '#f1c40f'                  // yellow-gold
    };
    const barKeys = ['Stripe', 'ACH', 'Venmo', 'CashApp', 'PayPal'];

    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [siteFilter, setSiteFilter] = useState('All Sites');

    /* ── fetch data ─────────────────────────────────────────── */
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await fetch('https://figazuresql.swancreate.com/api/swa/Payments');
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                setPayments(
                    data.map(p => ({
                        ...p,
                        PaymentDate: new Date(p.PaymentDate),
                        amt: Number(p.PaymentAmount)
                    }))
                );
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    /* ── site list & filter ─────────────────────────────────── */
    const sites = useMemo(() => {
        const names = Array.from(new Set(payments.map(p => p.SchoolName).filter(Boolean))).sort();
        return ['All Sites', ...names];
    }, [payments]);

    const filtered = useMemo(
        () => (siteFilter === 'All Sites'
            ? payments
            : payments.filter(p => p.SchoolName === siteFilter)),
        [payments, siteFilter]
    );

    /* ── KPI & source dist ──────────────────────────────────── */
    const kpis = useMemo(() => {
        const totalCollected = filtered.reduce((s, p) => s + p.amt, 0);
        const uniqueDates = new Set(filtered.map(p => p.PaymentDate.toISOString().slice(0, 10))).size;
        const avgDaily = uniqueDates ? totalCollected / uniqueDates : 0;
        const txCount = filtered.length;
        const uniqueStudents = new Set(filtered.map(p => p.StudentGuidID)).size;
        const sourceDist = filtered.reduce((acc, p) => {
            acc[p.PaymentSource] = (acc[p.PaymentSource] || 0) + 1;
            return acc;
        }, {});
        return { totalCollected, avgDaily, txCount, uniqueStudents, sourceDist };
    }, [filtered]);

    /* ── stacked bar: all sites ─────────────────────────────── */
    const barData = useMemo(() => {
        const stats = {};
        payments.forEach(p => {
            if (!stats[p.SchoolName]) {
                stats[p.SchoolName] = { site: p.SchoolName, total: 0, Stripe: 0, ACH: 0, Venmo: 0, CashApp: 0, PayPal: 0 };
            }
            stats[p.SchoolName][p.PaymentSource] += p.amt;
            stats[p.SchoolName].total += p.amt;
        });
        return Object.values(stats).sort((a, b) => b.total - a.total);
    }, [payments]);

    /* ── histogram buckets ──────────────────────────────────── */
    const histogramData = useMemo(() => {
        const buckets = {};
        filtered.forEach(p => {
            const bucket = Math.floor(p.amt / 10) * 10;
            const label = `${usdFmt.format(bucket)}–${usdFmt.format(bucket + 9)}`;
            buckets[label] = (buckets[label] || 0) + 1;
        });
        return Object.entries(buckets)
            .map(([bucket, count]) => ({ bucket, count }))
            .sort((a, b) => parseInt(a.bucket.replace(/[^0-9]/g, '')) - parseInt(b.bucket.replace(/[^0-9]/g, '')));
    }, [filtered]);

    /* ── line chart ─────────────────────────────────────────── */
    const lineData = useMemo(() => {
        const byDate = {};
        filtered.forEach(p => {
            const d = p.PaymentDate.toISOString().slice(0, 10);
            byDate[d] = (byDate[d] || 0) + p.amt;
        });
        const pts = Object.entries(byDate)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .map(([x, y]) => ({ x, y }));
        return [{ id: siteFilter, data: pts }];
    }, [filtered, siteFilter]);

    /* ── table rows ─────────────────────────────────────────── */
    const rows = useMemo(() => filtered.map((p, i) => ({
        id: i,
        date: p.PaymentDate.toLocaleString(),
        site: p.SchoolName,
        student: `${p.StudentFName} ${p.StudentLName}`,
        guardian: `${p.guardian_FName} ${p.guardian_LName}`,
        amount: usdFmt.format(p.amt),
        StudentNumber: p.StudentNumber,
        source: p.PaymentSource,
        reference: p.PaymentReference
    })), [filtered]);

    const columns = [
        { field: 'date', headerName: 'Date', flex: 1 },
        { field: 'site', headerName: 'Site', flex: 1 },
        { field: 'student', headerName: 'Student', flex: 1 },
        { field: 'StudentNumber', headerName: 'StudentId', flex: 1 },
        { field: 'guardian', headerName: 'Guardian', flex: 1 },
        { field: 'amount', headerName: 'Amount', flex: 0.7 },
        { field: 'source', headerName: 'Source', flex: 0.6 },
        { field: 'reference', headerName: 'Reference', flex: 1 }
    ];

    /* ── CSV download ───────────────────────────────────────── */
    const downloadCSV = () => {
        const hdr = columns.map(c => c.headerName);
        const rowsCsv = rows.map(r => columns.map(c => r[c.field]).join(','));
        const blob = new Blob([[hdr.join(',')].concat(rowsCsv).join('\n')], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'payments.csv';
        link.click();
    };

    /* ── shared tooltip style ───────────────────────────────── */
    const nivoTooltip = {
        container: {
            background: isDark ? '#2e2e2e' : '#ffffff',
            color: isDark ? '#fff' : '#000'
        }
    };

    /* ── card style ─────────────────────────────────────────── */
    const statCardSX = bg => ({
        flex: 1,
        textAlign: 'center',
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 0 10px 2px ${bg}88`
    });

    /* ── render ─────────────────────────────────────────────── */
    if (loading) return (
        <Box m={2} display="flex" justifyContent="center"><CircularProgress /></Box>
    );
    if (error) return (
        <Box m={2}><Typography color={theme.palette.error.main}>Error: {error}</Typography></Box>
    );

    return (
        <Box m="20px">
            <Typography variant="h4" fontWeight="bold" mb="20px" color={text}>
                Payment Tracking Dashboard
            </Typography>

            {/* Site selector */}
            <Box mb={3}>
                <TextField
                    select
                    label="Site"
                    value={siteFilter}
                    onChange={e => setSiteFilter(e.target.value)}
                    SelectProps={{ native: true }}
                    sx={{ minWidth: 240 }}
                >
                    {sites.map(s => <option key={s} value={s}>{s}</option>)}
                </TextField>
            </Box>

            {/* KPI cards */}
            <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
                {[
                    { label: 'Total Collected', value: usdFmt.format(kpis.totalCollected) },
                    { label: 'Avg per Day', value: usdFmt.format(kpis.avgDaily) },
                    { label: '# Transactions', value: kpis.txCount },
                    { label: 'Unique Students', value: kpis.uniqueStudents }
                ].map(({ label, value }) => (
                    <Card key={label} sx={statCardSX(isDark ? '#ffffff' : '#000000')}>
                        <CardContent>
                            <Typography variant="h5" color={text}>{value}</Typography>
                            <Typography color={text}>{label}</Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* source KPI row */}
            <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
                {Object.entries(kpis.sourceDist).map(([src, count]) => (
                    <Card key={src} sx={statCardSX(sourceColors[src])}>
                        <CardContent>
                            <Typography variant="h5" color={text}>{count}</Typography>
                            <Typography color={text}>{src}</Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* grid: histogram + line */}
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={4} mb={4}>

                {/* histogram */}
                <Paper sx={{ p: 2 }}>
                    <Typography mb={2} color={text}>Payment Amount Distribution</Typography>
                    <Box height={300}>
                        <ResponsiveBar
                            data={histogramData}
                            keys={['count']}
                            indexBy="bucket"
                            margin={{ top: 20, right: 20, bottom: 80, left: 60 }}
                            padding={0.2}
                            colors={isDark ? theme.palette.success.light : theme.palette.success.main}
                            axisBottom={{ tickRotation: -45 }}
                            enableLabel={false}
                            theme={{
                                axis: { ticks: { text: { fill: text } }, legend: { text: { fill: text } } },
                                labels: { text: { fill: text } },
                                tooltip: nivoTooltip
                            }}
                        />
                    </Box>
                </Paper>

                {/* line chart */}
                <Paper sx={{ p: 2 }}>
                    <Typography mb={2} color={text}>{siteFilter} Payments Over Time</Typography>
                    <Box height={300}>
                        <ResponsiveLine
                            data={lineData}
                            margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
                            xScale={{ type: 'time', format: '%Y-%m-%d', useUTC: false }}
                            xFormat="time:%Y-%m-%d"
                            yScale={{ type: 'linear', min: 0 }}
                            axisBottom={{
                                format: '%b %d',
                                tickRotation: -45,
                                legend: 'Date',
                                legendOffset: 36,
                                legendPosition: 'middle',
                                tickPadding: 5
                            }}
                            axisLeft={{
                                legend: 'Total Amount',
                                legendOffset: -40,
                                tickFormat: v => usdFmt.format(v)
                            }}
                            colors={isDark ? theme.palette.success.light : theme.palette.success.main}
                            theme={{
                                axis: { ticks: { text: { fill: isDark ? '#fff' : text } }, legend: { text: { fill: isDark ? '#fff' : text } } },
                                textColor: isDark ? '#fff' : text,
                                tooltip: nivoTooltip
                            }}
                            curve="monotoneX"
                        />
                    </Box>
                </Paper>
            </Box>

            {/* stacked bar (all sites) */}
            <Paper sx={{ p: 2, mb: 4 }}>
                <Typography mb={2} color={text}>
                    Sites by Total Collected (click to filter)
                </Typography>
                <Box height={450}>
                    <ResponsiveBar
                        data={barData}
                        keys={barKeys}
                        indexBy="site"
                        groupMode="stacked"
                        margin={{ top: 20, right: 20, bottom: 150, left: 60 }}
                        padding={0.3}
                        colors={({ id }) => sourceColors[id]}
                        theme={{
                            axis: { ticks: { text: { fill: text } }, legend: { text: { fill: text } } },
                            labels: { text: { fill: text } },
                            tooltip: nivoTooltip
                        }}
                        axisBottom={{ tickRotation: -45 }}
                        axisLeft={{ tickFormat: v => usdFmt.format(v) }}
                        enableLabel={false}
                        tooltip={({ id, value, indexValue }) => (
                            <div style={{
                                padding: '6px 9px',
                                background: isDark ? '#2e2e2e' : '#ffffff',
                                color: isDark ? '#fff' : '#000'
                            }}>
                                <strong>{indexValue}</strong><br />{id}: {usdFmt.format(value)}
                            </div>
                        )}
                        onClick={bar => {
                            const clicked = bar.data.site;
                            setSiteFilter(prev => (prev === clicked ? 'All Sites' : clicked));
                        }}
                        legends={[{
                            anchor: 'bottom',
                            direction: 'row',
                            translateY: 56,
                            itemWidth: 60,
                            itemHeight: 14,
                            itemsSpacing: 10,
                            symbolSize: 14,
                            itemTextColor: text,
                            data: barKeys.map(k => ({ id: k, label: k, color: sourceColors[k] }))
                        }]}
                    />
                </Box>
            </Paper>

            {/* CSV + table */}
            <Button
                variant="contained"
                sx={{
                    mb: 2,
                    backgroundColor: theme.palette.success.main,
                    '&:hover': { backgroundColor: theme.palette.success.light }
                }}
                onClick={downloadCSV}
            >
                Download CSV
            </Button>
            <Box height={600}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    sortModel={[{ field: 'site', sort: 'asc' }]}
                    sx={{
                        '.MuiDataGrid-row:nth-of-type(even)': { backgroundColor: theme.palette.action.hover },
                        '.MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: theme.palette.background.default },
                        '.MuiDataGrid-columnHeaders': { backgroundColor: theme.palette.background.paper },
                        '.MuiDataGrid-row:hover': {
                            backgroundColor: isDark ? theme.palette.action.selected : theme.palette.action.hover
                        },
                        '.MuiDataGrid-cell': { color: text }
                    }}
                />
            </Box>
        </Box>
    );
};

export default PaymentTrackingDashboard;
